<template>
  <div>
    <div class="d-flex align-center">
      <div class="tp-approver__intro">
        <div class="text-subtitle-1">承認者・提出期限</div>
        <div class="text-caption text-grey-darken-1">
          帳票種別とクラスの組み合わせごとに承認者と提出期限を決めます。クラスを「全クラス共通」にすると、
          個別設定のないクラスに適用されます。最終承認者を「設定しない」にすると一段階承認になります。
        </div>
      </div>
      <v-spacer />
      <span v-if="isDirty" class="text-caption text-warning mr-3 tp-approver__dirty">
        <v-icon size="14">mdi-circle-medium</v-icon>
        未保存の変更があります
      </span>
      <v-btn class="mr-2" variant="outlined" color="primary" :disabled="loading || loadFailed" @click="addRow">
        <v-icon start>mdi-plus</v-icon>
        設定を追加
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        :loading="saving"
        :disabled="!isValid || loading || loadFailed"
        @click="save"
      >
        保存する
      </v-btn>
    </div>

    <v-alert v-if="duplicatedKeys.length > 0" type="warning" variant="tonal" density="compact" class="mt-3">
      同じ帳票種別・クラスの設定が重複しています。1つにまとめてください。
    </v-alert>

    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <v-alert v-else-if="loadFailed" type="error" variant="tonal" density="compact" class="mt-3">
      承認者・提出期限の設定を読み込めませんでした。通信状況を確認して、もう一度お試しください。
      <template #append>
        <v-btn size="small" variant="outlined" @click="load">再読み込み</v-btn>
      </template>
    </v-alert>

    <v-table v-else density="compact" class="mt-3 tp-approver__table">
      <thead>
        <!--
          列に固定幅や割合を振ると、どこかのセレクトが必ず「全クラ…」と省略される。
          幅は各セレクトの min-width で決め、表には配分させない。
          合計は1440px想定の本文幅に収まる。
        -->
        <tr>
          <th>帳票種別</th>
          <th>クラス</th>
          <th>一次承認者</th>
          <th>最終承認者</th>
          <th>提出期限</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, index) in rows" :key="index">
          <td>
            <v-select
              v-model="row.planType"
              :items="createPlanTypeItems"
              variant="outlined"
              density="compact"
              hide-details
              color="primary"
              @update:model-value="onPlanTypeChange(row)"
            
              style="flex: none; width: 150px"
            ></v-select>
          </td>
          <td>
            <v-select
              v-model="row.classId"
              :items="classItemsFor(row.planType)"
              variant="outlined"
              density="compact"
              hide-details
              color="primary"
              :disabled="row.planType === 'overall'"
              style="flex: none; width: 220px"
            ></v-select>
          </td>
          <td>
            <v-select
              v-model="row.firstApproverId"
              :items="approverItems"
              variant="outlined"
              density="compact"
              hide-details
              color="primary"
              no-data-text="主任以上のスタッフがいません"
            
              style="flex: none; width: 150px"
            ></v-select>
          </td>
          <td>
            <v-select
              v-model="row.finalApproverId"
              :items="finalApproverItems"
              variant="outlined"
              density="compact"
              hide-details
              color="primary"
            
              style="flex: none; width: 150px"
            ></v-select>
          </td>
          <td>
            <!-- 折り返すと行の高さが揃わず読みにくいので1行に収める -->
            <div class="d-flex align-center ga-2 flex-nowrap">
              <v-select
                v-model="row.deadlineKind"
                :items="deadlineKindItemsFor(row.planType)"
                variant="outlined"
                density="compact"
                hide-details
                color="primary"
                style="flex: none; width: 150px"
              ></v-select>
              <v-text-field
                v-if="row.deadlineKind === 'dayOfPreviousMonth'"
                v-model.number="row.deadlineDay"
                type="number"
                min="1"
                max="28"
                suffix="日"
                variant="outlined"
                density="compact"
                hide-details
                color="primary"
                style="flex: none; width: 95px"
              ></v-text-field>
              <v-select
                v-if="row.deadlineKind === 'weekdayOfPreviousWeek'"
                v-model="row.deadlineWeekday"
                :items="weekdayItems"
                variant="outlined"
                density="compact"
                hide-details
                color="primary"
                style="flex: none; width: 120px"
              ></v-select>
              <v-text-field
                v-if="row.deadlineKind === 'fixedDate'"
                v-model="row.deadlineMonthDay"
                placeholder="MM-DD"
                variant="outlined"
                density="compact"
                hide-details
                color="primary"
                style="flex: none; width: 110px"
              ></v-text-field>
            </div>
            <div v-if="deadlineError(row)" class="text-caption text-error mt-1">
              {{ deadlineError(row) }}
            </div>
          </td>
          <td class="text-right">
            <v-btn size="x-small" variant="text" color="error" @click="removeRow(index)">
              削除
            </v-btn>
          </td>
        </tr>
        <tr v-if="rows.length === 0">
          <td colspan="6" class="text-center text-grey py-6">
            承認者が設定されていません。設定するまで指導計画を提出できません。
          </td>
        </tr>
      </tbody>
    </v-table>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import {
  NurseryClassResponse,
  NurseryTeachingPlanDeadlineRule,
  NurseryTeachingPlanType,
} from "chaild-api/lib";
import ApiClass from "@/api/ApiClass";
import ApiNurseryUser from "@/api/ApiNurseryUser";
import ApiTeachingPlan from "@/api/ApiTeachingPlan";
import { createPlanTypeItems, deadlineRuleKindItems, weekdayItems } from "@/constants/teachingPlan";
import { notifySuccess } from "@/utils/TeachingPlanDialog";
import TeachingPlanUtil from "@/utils/TeachingPlanUtil";

type DeadlineKind = "none" | "dayOfPreviousMonth" | "weekdayOfPreviousWeek" | "fixedDate";

type ApproverRow = {
  planType: NurseryTeachingPlanType;
  classId: string | null;
  firstApproverId: number | null;
  finalApproverId: number | null;
  deadlineKind: DeadlineKind;
  deadlineDay: number;
  deadlineWeekday: number;
  deadlineMonthDay: string;
};

/**
 * 承認者・提出期限の設定 (api-design.md §5.7 #23)。
 * 保存は園単位の一括置換なので、画面の行がそのまま設定の全量になる。
 */
export default defineComponent({
  name: "TeachingPlanApproverTab",

  props: {
    nurseryId: {
      type: Number,
      required: true,
    },
  },

  async mounted() {
    await this.loadStaffs();
    await this.load();
  },

  data() {
    return {
      loading: false,
      saving: false,
      /** 一覧取得の失敗フラグ。「設定0件」と区別して保存を封鎖する */
      loadFailed: false,
      rows: [] as ApproverRow[],
      /**
       * 最後に読み込み・保存した内容 (未保存判定用)。
       * このタブは v-if で破棄されるため、持たないとタブ切替で黙って消える。
       *
       * null は「まだ比較できる状態が無い」(読み込み前・取得失敗) を表す。
       * 空文字を初期値にすると rows=[] と食い違って未保存扱いになるため null にする。
       */
      savedSnapshot: null as string | null,
      classes: [] as NurseryClassResponse[],
      staffs: [] as { userId: number; name: string; isChiefOrAbove: boolean }[],
      createPlanTypeItems,
      weekdayItems,
    };
  },

  computed: {
    /** 承認者は主任以上のみ (サーバ側でも検証される) */
    approverItems(): { title: string; value: number }[] {
      return this.staffs
        .filter((staff) => staff.isChiefOrAbove)
        .map((staff) => ({ title: staff.name, value: staff.userId }));
    },
    /**
     * 最終承認者。
     * 「設定しない（一段階承認）」は列幅に収まらず「設定しな…」と省略されていたため、
     * 選択肢は短くし、意味は見出しの説明文で補う。
     */
    finalApproverItems(): { title: string; value: number | null }[] {
      return [{ title: "設定しない", value: null }, ...this.approverItems];
    },
    /** 帳票種別×クラスの重複 */
    duplicatedKeys(): string[] {
      const seen = new Map<string, number>();
      for (const row of this.rows) {
        const key = `${row.planType}:${row.classId ?? "all"}`;
        seen.set(key, (seen.get(key) || 0) + 1);
      }
      return Array.from(seen.entries())
        .filter(([, count]) => count > 1)
        .map(([key]) => key);
    },
    /** 未保存の変更があるか (親のタブ切替・離脱確認が参照する) */
    isDirty(): boolean {
      // 読み込み前・取得失敗中は比べる相手がいない。ここを「未保存」にすると、
      // 応答待ちの一瞬だけ警告が出たり、保存できないのに離脱確認が出続けたりする
      if (this.savedSnapshot === null) {
        return false;
      }
      return JSON.stringify(this.rows) !== this.savedSnapshot;
    },
    isValid(): boolean {
      if (this.duplicatedKeys.length > 0) {
        return false;
      }
      return this.rows.every(
        (row) => row.firstApproverId !== null && this.deadlineError(row) === ""
      );
    },
  },

  methods: {
    classItemsFor(planType: NurseryTeachingPlanType): { title: string; value: string | null }[] {
      if (planType === "overall") {
        return [{ title: "園全体", value: null }];
      }
      return [
        { title: "全クラス共通", value: null },
        // 全年度のクラスが並ぶため、同名クラスを見分けられるよう年度を添える
        ...this.classes.map((item) => ({
          title: TeachingPlanUtil.classLabel(item),
          value: String(item.classId),
        })),
      ];
    },
    /** 帳票種別に対応しない期限ルールは選ばせない (サーバ側は期限なし扱いになる) */
    deadlineKindItemsFor(planType: NurseryTeachingPlanType) {
      return deadlineRuleKindItems.filter((item) => {
        if (item.value === "none") {
          return true;
        }
        if (item.value === "dayOfPreviousMonth") {
          return planType === "monthly";
        }
        if (item.value === "weekdayOfPreviousWeek") {
          return planType === "weekly";
        }
        return planType === "overall" || planType === "annual";
      });
    },
    deadlineError(row: ApproverRow): string {
      if (row.deadlineKind === "dayOfPreviousMonth") {
        return Number.isInteger(row.deadlineDay) && row.deadlineDay >= 1 && row.deadlineDay <= 28
          ? ""
          : "1〜28の日付を指定してください。";
      }
      if (row.deadlineKind === "fixedDate") {
        if (!/^\d{2}-\d{2}$/.test(row.deadlineMonthDay)) {
          return "MM-DD の形式で入力してください。";
        }
        const [month, day] = row.deadlineMonthDay.split("-").map(Number);
        // 平年基準で実在日を検証する (02-29は非閏年に期限が定まらないため不可)
        const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
        if (month < 1 || month > 12 || day < 1 || day > daysInMonth[month - 1]) {
          return "実在する日付を指定してください（2月29日は指定できません）。";
        }
      }
      return "";
    },
    onPlanTypeChange(row: ApproverRow) {
      if (row.planType === "overall") {
        row.classId = null;
      }
      const allowed = this.deadlineKindItemsFor(row.planType).map((item) => item.value);
      if (!allowed.includes(row.deadlineKind)) {
        row.deadlineKind = "none";
      }
    },
    addRow() {
      this.rows.push({
        planType: "monthly",
        classId: null,
        firstApproverId: this.approverItems.length > 0 ? this.approverItems[0].value : null,
        finalApproverId: null,
        deadlineKind: "none",
        deadlineDay: 25,
        deadlineWeekday: 5,
        deadlineMonthDay: "04-30",
      });
    },
    removeRow(index: number) {
      this.rows.splice(index, 1);
    },
    /**
     * クラス一覧。API は配列をそのまま返す ({ classes } で包まれていない)。
     * 取得失敗は null で返し、呼び出し元 (load) が保存を封鎖する。
     * クラス名が見えない状態で編集・保存すると園単位の一括置換で設定事故になるため。
     */
    async loadClasses(): Promise<NurseryClassResponse[] | null> {
      const response: unknown = await ApiClass.listClass({ nurseryId: this.nurseryId }).catch(
        () => null
      );
      return Array.isArray(response) ? (response as NurseryClassResponse[]) : null;
    },
    async loadStaffs() {
      const users = await ApiNurseryUser.listStaffs(this.nurseryId, {}).catch(() => []);
      this.staffs = users.map((user) => ({
        userId: user.userId,
        name: `${user.lastName} ${user.firstName}`,
        isChiefOrAbove: user.isChief || user.isDirector || user.isAdmin,
      }));
    },
    async load() {
      this.loading = true;
      this.loadFailed = false;
      // クラス一覧も同時に取り直す (再読み込みボタンからも復旧できるように)
      const [classes, response] = await Promise.all([
        this.loadClasses(),
        ApiTeachingPlan.listApproverSettings({
          nurseryId: this.nurseryId,
        }).catch(() => null),
      ]);
      this.loading = false;
      if (classes !== null) {
        this.classes = classes;
      }
      if (!response || classes === null) {
        // 取得失敗を「設定0件」と区別する。空のまま保存できると園単位の一括置換で
        // 既存設定を全て消してしまうため、保存を封鎖して再読み込みを促す
        this.loadFailed = true;
        // 保存できない状態で未保存扱いにしない (離脱確認だけが出続ける)
        this.savedSnapshot = null;
        return;
      }
      this.rows = response.data.map((item) => {
        const rule = item.deadlineRule;
        return {
          planType: item.planType,
          classId: item.classId,
          firstApproverId: item.firstApproverId,
          finalApproverId: item.finalApproverId,
          deadlineKind: (rule ? rule.kind : "none") as DeadlineKind,
          deadlineDay: rule && rule.kind === "dayOfPreviousMonth" ? rule.day : 25,
          deadlineWeekday: rule && rule.kind === "weekdayOfPreviousWeek" ? rule.weekday : 5,
          deadlineMonthDay: rule && rule.kind === "fixedDate" ? rule.monthDay : "04-30",
        };
      });
      this.savedSnapshot = JSON.stringify(this.rows);
    },
    buildDeadlineRule(row: ApproverRow): NurseryTeachingPlanDeadlineRule | null {
      switch (row.deadlineKind) {
        case "dayOfPreviousMonth":
          return { kind: "dayOfPreviousMonth", day: row.deadlineDay };
        case "weekdayOfPreviousWeek":
          return { kind: "weekdayOfPreviousWeek", weekday: row.deadlineWeekday };
        case "fixedDate":
          return { kind: "fixedDate", monthDay: row.deadlineMonthDay };
        default:
          return null;
      }
    },
    async save() {
      if (!this.isValid || this.loading || this.loadFailed) {
        return;
      }
      this.saving = true;
      const response = await ApiTeachingPlan.upsertApproverSettings({
        nurseryId: this.nurseryId,
        settings: this.rows.map((row) => ({
          planType: row.planType,
          classId: row.classId,
          firstApproverId: row.firstApproverId as number,
          finalApproverId: row.finalApproverId,
          deadlineRule: this.buildDeadlineRule(row),
        })),
      }).catch(() => null);
      this.saving = false;
      if (response) {
        await this.load();
        notifySuccess("承認者・提出期限を保存しました");
      }
    },
  },
});
</script>

<style scoped>
/*
 * 6列すべてに実寸のコントロールが入るため、既定のセル余白 (左右16px) だと
 * 合計が本文幅を超えて右端の削除列が押し出される。余白側を詰める。
 */
.tp-approver__table :deep(.v-table__wrapper > table > tbody > tr > td),
.tp-approver__table :deep(.v-table__wrapper > table > thead > tr > th) {
  padding-left: 6px;
  padding-right: 6px;
}
/* 説明文が右の操作ボタンの下へ回り込まないようにする */
.tp-approver__intro {
  max-width: 62%;
}
.tp-approver__dirty {
  white-space: nowrap;
}
</style>
